import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { announcementService } from '../../services/announcementService';

const AnnouncementDetailModal = ({ announcement, isOpen, onClose, onAcknowledge }) => {
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // Track view when modal opens
  useEffect(() => {
    if (isOpen && announcement) {
      trackView();
    }
  }, [isOpen, announcement]);

  const trackView = async () => {
    if (!isAuthenticated || !announcement) return;
    
    try {
      // Track view through the announcement service
      await announcementService.trackView(announcement.id);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const handleAcknowledge = async () => {
    if (!isAuthenticated || !announcement) return;
    
    setLoading(true);
    try {
      await announcementService.acknowledge(announcement.id);
      setAcknowledged(true);
      showSuccess('Announcement acknowledged');
      if (onAcknowledge) {
        onAcknowledge(announcement.id);
      }
    } catch (error) {
      console.error('Error acknowledging announcement:', error);
      showError('Failed to acknowledge announcement');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (!isAuthenticated || !announcement) return;
    
    try {
      await announcementService.trackClick(announcement.id);
    } catch (error) {
      console.error('Error tracking click:', error);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800 border-blue-200',
      warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      success: 'bg-green-100 text-green-800 border-green-200',
      error: 'bg-red-100 text-red-800 border-red-200',
      maintenance: 'bg-orange-100 text-orange-800 border-orange-200',
      update: 'bg-purple-100 text-purple-800 border-purple-200',
      event: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      alert: 'bg-pink-100 text-pink-800 border-pink-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen || !announcement) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">
                  {announcement.title}
                </h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTypeColor(announcement.announcement_type)}`}>
                  {announcement.announcement_type}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(announcement.priority)}`}>
                  {announcement.priority}
                </span>
              </div>
              
              {announcement.summary && (
                <p className="text-gray-600 text-sm">
                  {announcement.summary}
                </p>
              )}
            </div>
            
            <button
              onClick={onClose}
              className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Announcement Content */}
            <div className="mb-6">
              <div 
                className="prose max-w-none text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: announcement.content || announcement.summary || 'No content available' 
                }}
              />
            </div>

            {/* Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Timing</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Start:</span> {formatDate(announcement.start_date)}
                  </div>
                  {announcement.end_date && (
                    <div>
                      <span className="font-medium">End:</span> {formatDate(announcement.end_date)}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Created:</span> {formatDate(announcement.created_at)}
                  </div>
                  {announcement.published_at && (
                    <div>
                      <span className="font-medium">Published:</span> {formatDate(announcement.published_at)}
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Targeting & Display</h4>
                <div className="space-y-1 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Scope:</span> {announcement.is_global ? 'Global' : `Society: ${announcement.society?.name || 'Unknown'}`}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {announcement.show_on_homepage && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">Homepage</span>
                    )}
                    {announcement.show_in_header && (
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">Header</span>
                    )}
                    {announcement.show_as_popup && (
                      <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">Popup</span>
                    )}
                    {announcement.show_in_sidebar && (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Sidebar</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{announcement.view_count || 0}</div>
                <div className="text-sm text-blue-800">Views</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{announcement.click_count || 0}</div>
                <div className="text-sm text-green-800">Clicks</div>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{announcement.acknowledgment_count || 0}</div>
                <div className="text-sm text-purple-800">Acknowledged</div>
              </div>
            </div>

            {/* Author Info */}
            {announcement.author && (
              <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg mb-6">
                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium text-gray-900">{announcement.author.name || announcement.author.email}</div>
                  <div className="text-sm text-gray-600">Author</div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div className="flex items-center space-x-4">
              {isAuthenticated && !acknowledged && (
                <button
                  onClick={handleAcknowledge}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  Acknowledge
                </button>
              )}
              
              {acknowledged && (
                <div className="flex items-center text-green-600">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Acknowledged
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementDetailModal;
